/** @jsxImportSource @emotion/react */
import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation} from "react-i18next";
import {Icon, Menu, MenuItem, Spinner} from "@blueprintjs/core";

// Libs
import Button from "../common/components/button";
import {primaryColor} from "../common/libs/theme-lib";

// Components
import TextInput from "../common/components/text-input";
import RangeSlider from "../common/components/range-slider";
import RenderContent from "../common/components/render-content";
import SelectInput from "../common/components/select-input";
import ClientSelector from "../common/components/client-selector";
import { Popover2 } from "@blueprintjs/popover2";

// Hooks
import {useAuth} from "../auth/auth";
import {useFilters} from "../filters/filters";
import {useUserClientsData} from "../user-clients-data/user-clients-data";

// Actions
import {readTechnicalsSheets} from "./technicals-sheets-actions";
import {downloadPdf} from "../content/content-actions";

// Selectors
import {technicalsSheetsSelector, technicalsSheetsIsFetchingSelector, technicalsSheetsErrorSelector} from "./technicals-sheets-selectors";
import {contentIsFetchingSelector, contentIdSelector, contentTypeSelector} from "../content/content-selectors";

// Images
import spainImage from "../assets/icons8-spain-48.png";
import britainImage from "../assets/icons8-great-britain-48.png";
import germanyImage from "../assets/icons8-germany-48.png";
import franceImage from "../assets/icons8-france-48.png";

// Component Selector
const technicalsSheetsPageSelector = createSelector(
    technicalsSheetsSelector,
    technicalsSheetsIsFetchingSelector,
    technicalsSheetsErrorSelector,
    contentIsFetchingSelector,
    contentIdSelector,
    contentTypeSelector,
    (technicalsSheets, technicalsSheetsIsFetching, technicalsSheetsError, contentIsFetching, contentId, contentType) => {
        return {
            technicalsSheets: technicalsSheets?.toJS() || [],
            technicalsSheetsIsFetching,
            technicalsSheetsError,
            contentIsFetching,
            contentId,
            contentType
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readTechnicalsSheets: (filters) => dispatch(readTechnicalsSheets(filters)),
        downloadPdf: (pdfId, pdfType, clientId, filename, language) => dispatch(downloadPdf(pdfId, pdfType, clientId, filename, language))
    }
};

const LanguagePopover = (props) => {
    const {row} = props;

    const [downloadIsOpen, setDownloadIsOpen] = useState(false);

    const {selectedClient} = useAuth();

    const {downloadPdf} = useReduxDispatch();

    let filename;
    if (row.material_type === "ZTAP" || row.material_type === "ZBOT") {
        filename = `${row.client_material}.pdf`;
    }
    else if (row.file_name) {
        filename = row.file_name;
    }

    const pdfId = row._id;

    return (
        <Popover2
            isOpen={downloadIsOpen}
            onClose={() => setDownloadIsOpen(false)}
            content={
                <Menu style={{ minWidth: "auto", maxWidth: "none" }}>
                    <MenuItem text="Español" icon={<img src={spainImage} width={20} height={20} alt={"Bandera Española"} />} onClick={() => downloadPdf(pdfId, "technical-sheet", selectedClient, filename, "S")} />
                    <MenuItem text="Français" icon={<img src={franceImage} width={20} height={20} alt={"Drapeau Français"} />} onClick={() => downloadPdf(pdfId, "technical-sheet", selectedClient, filename, "F")} />
                    <MenuItem text="Deutsch" icon={<img src={germanyImage} width={20} height={20} alt={"Deutsche Flagge"} />} onClick={() => downloadPdf(pdfId, "technical-sheet", selectedClient, filename, "D")} />
                    <MenuItem text="English" icon={<img src={britainImage} width={20} height={20} alt={"English flag"} />} onClick={() => downloadPdf(pdfId, "technical-sheet", selectedClient, filename, "E")} />
                </Menu>
            }
        >
            <Icon icon="download" iconSize={20} onClick={() => setDownloadIsOpen(true)} />
        </Popover2>
    )
}

const TechnicalSheetsPage = (props) => {
    const auth = useAuth();

    // filters
    const {technicalSheetsFilters, setTechnicalSheetsFilters} = useFilters();

    // Client Selector
    const {selectedClient} = useAuth();
    const {userClientsData} = useUserClientsData();
    const selectedClientData = userClientsData.find(client => client._id === selectedClient);

    // Redux
    const {
        technicalsSheets, technicalsSheetsIsFetching, technicalsSheetsError,
        contentIsFetching, contentId, contentType
    } = useSelector(state => technicalsSheetsPageSelector(state));
    const {readTechnicalsSheets} = useReduxDispatch();

    // filters state
    const [material, setMaterial] = useState(technicalSheetsFilters?.material || "");
    const [description, setDescription] = useState(technicalSheetsFilters?.description || "");
    const [documentId, setDocumentId] = useState(technicalSheetsFilters?.documentId || "");

    //i18n
    const {t} = useTranslation("technicalsSheets");

    // state of showWelcomeMessage
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(!technicalSheetsFilters);

    const materialType = [
        {
            value: "all",
            label: t("filters.all")
        },
        {
            value: "ZTAP",
            label: t("filters.ztap")
        },
        {
            value: "ZAUX",
            label: t("filters.zaux")
        },
        {
            value: "ZBOT",
            label: t("filters.zbot")
        }
    ];

    // set materialType selected
    const [selectedMaterialType, setSelectedMaterialType] = useState(technicalSheetsFilters?.materialType || materialType[0].value);
    // material type selected
    const selectedMaterialTypeData = materialType.find(materialType => materialType.value === selectedMaterialType);
    // render header text
    const [renderHeaderText, setRenderHeaderText] = useState("technicalsSheets:technicalsSheetsHeader");

    // range slider
    const range = [0, 300], heightRange = [0, 400];
    const [rangeDiameter, setRangeDiameter] = useState(technicalSheetsFilters?.diameter || range);
    const [rangeHeight, setRangeHeight] = useState(technicalSheetsFilters?.height || heightRange);

    return (
        <>
            <div style={{
                color: primaryColor,
                fontSize: 28,
                fontWeight: "bold",
                marginBottom: 10
            }}>{t("title", { client: selectedClientData && selectedClientData.name } )}</div>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                {auth.userType === "admin" && (
                    <ClientSelector />
                )}
                <SelectInput
                    options={materialType}
                    label={t("filters.materialType")}
                    placeholder={t("filters.materialType")}
                    onChange={(selectedMaterialType) => setSelectedMaterialType(selectedMaterialType.value)}
                    value={selectedMaterialTypeData}
                    style={{marginRight: 15}}
                />
                <TextInput
                    label={t("filters.material")}
                    style={{marginRight: 15, minWidth: 100, width: 130}}
                    value={material}
                    setValue={setMaterial}
                />
                <TextInput
                    label={t("filters.description")}
                    style={{marginRight: 15, minWidth: 100, width: 130}}
                    value={description}
                    setValue={setDescription}
                />
                <TextInput
                    label={t("filters.technicalSheetNumber")}
                    style={{marginRight: 15, minWidth: 100, width: 130}}
                    value={documentId}
                    setValue={setDocumentId}
                />
                <RangeSlider
                    style={{marginRight: 30, marginLeft: 10, width: 150}}
                    label={t("filters.diameter")}
                    min={range[0]}
                    max={range[1]}
                    labelStepSize={range[1]}
                    value={rangeDiameter}
                    onRelease={(value) => setRangeDiameter(value)}
                    onChange={(value) => setRangeDiameter(value)}
                />
                <RangeSlider
                    style={{marginRight: 30, marginLeft: 10, width: 150}}
                    label={t("filters.height")}
                    min={heightRange[0]}
                    max={heightRange[1]}
                    labelStepSize={heightRange[1]}
                    value={rangeHeight}
                    onRelease={(value) => setRangeHeight(value)}
                    onChange={(value) => setRangeHeight(value)}
                />
                <Button
                    text={t("common:actions.search")}
                    action={() => {
                        const filters = {
                            documentId: documentId,
                            material: material,
                            description: description,
                            diameter: rangeDiameter,
                            height: rangeHeight,
                            materialType: selectedMaterialTypeData ? selectedMaterialTypeData.value : ""
                        };
                        readTechnicalsSheets({...filters, client: selectedClient});
                        setTechnicalSheetsFilters(filters);
                        setShowWelcomeMessage(false);
                        if (selectedMaterialType) {
                            if (selectedMaterialTypeData.value === "ZBOT") {
                                setRenderHeaderText("technicalsSheets:technicalsSheetsZBotHeader");
                            } else if (selectedMaterialTypeData.value === "ZTAP") {
                                setRenderHeaderText("technicalsSheets:technicalsSheetsZTapHeader");
                            } else if (selectedMaterialTypeData.value === "ZAUX") {
                                setRenderHeaderText("technicalsSheets:technicalsSheetsZAuxHeader");
                            } else {
                                setRenderHeaderText("technicalsSheets:technicalsSheetsHeader");
                            }
                        }
                    }}
                    intent="primary"
                />
            </div>
            <RenderContent
                nameCollection={t("nameCollection")}
                data={technicalsSheets}
                showWelcomeMessage={showWelcomeMessage}
                renderHeader={true}
                renderHeaderText={renderHeaderText}
                renderHeaderValues={{count: technicalsSheets.length}}
                renderHeaderComponents={[<strong>number</strong>]}
                config={{
                    sortable: true,
                    showFooter: true,
                    initialState: {sortBy: [{id: "date", desc: true}]}
                }}
                columns={[{
                    id: "materialType",
                    Header: t("technicalsSheetsTableColumns.material"),
                    accessor: "client_material",
                    style: {maxWidth: 250}
                }, {
                    id: "description",
                    Header: t("technicalsSheetsTableColumns.description"),
                    accessor: "customer_description"
                }, {
                    id: "codeAC",
                    Header: t("technicalsSheetsTableColumns.codeAC"),
                    accessor: "material",
                    style: {maxWidth: 250}
                }, {
                    id: "packingStandard",
                    Header: t("technicalsSheetsTableColumns.packingStandard"),
                    style: {maxWidth: 100},
                    accessor: "packing_standard"
                }, {
                    id: "diameter",
                    Header: t("technicalsSheetsTableColumns.diameter"),
                    accessor: "diameter",
                    style: {maxWidth: 100},
                    type: "number"
                }, {
                    id: "height",
                    Header: t("technicalsSheetsTableColumns.height"),
                    accessor: "height",
                    style: {maxWidth: 100},
                    type: "number"
                }, {
                    id: "actions",
                    Header: t("technicalsSheetsTableColumns.download"),
                    accessor: row => row,
                    type: "function",
                    disableSortBy: true,
                    minWidth: 150,
                    width: 150,
                    style: {
                        maxWidth: 60,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    },
                    nodeContent: row => {

                        return contentIsFetching && contentType === "technical-sheet" && contentId === row._id ? (
                            <div>
                                <Spinner
                                    size={20}
                                    intent={"danger"}
                                />
                            </div>
                        ) : (
                            <div css={{
                                cursor: "pointer",
                                transition: "all 0.4s",
                                "&:hover": {
                                    color: primaryColor
                                }
                            }}>
                                <LanguagePopover row={row} />
                            </div>
                        );
                    }
                }]}
            />
        </>
    );
};

export default TechnicalSheetsPage;